import { useState } from 'react';
import { Warehouse } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SellerAccordionRow } from '~/components/common/SellerAccordionRow';
import { SellerRowHeader } from '~/components/common/SellerRowHeader';
import {
  SellerOrganizationPicker,
  type PickableSellerOrganization,
} from '~/components/common/SellerOrganizationPicker';
import { sellerLabel } from '~/utils';
import {
  shipFromFacilityChoices,
  type FacilityChoice,
} from '~/utils/facilityOptions';
import { toggleId } from '~/utils/toggleId';
import { useBuyListSellerPickerLabels } from '~/components/common/useBuyListSellerPickerLabels';

export type SelectedSellerFacility = {
  organizationId: string;
  facilityId: string;
  organizationLabel: string;
  facilityLabel: string;
  defaultCurrency: string;
};

type SelectSellerFacilityStepProps = {
  selectedFacilityId: string | null;
  onSelectFacility: (facility: SelectedSellerFacility) => void;
};

type SellerFacilityRowProps = {
  organization: PickableSellerOrganization;
  expanded: boolean;
  onToggleExpand: () => void;
  selectedFacilityId: string | null;
  onSelectFacility: (facility: SelectedSellerFacility) => void;
};

function toSelectedSellerFacility(
  organization: PickableSellerOrganization,
  { id, label }: FacilityChoice
): SelectedSellerFacility {
  return {
    organizationId: organization.id,
    facilityId: id,
    organizationLabel: sellerLabel(organization),
    facilityLabel: label,
    defaultCurrency: organization.defaultCurrency,
  };
}

function SellerFacilityRow({
  organization,
  expanded,
  onToggleExpand,
  selectedFacilityId,
  onSelectFacility,
}: Readonly<SellerFacilityRowProps>) {
  const { t } = useTranslation('inventory');
  const facilities = organization.facilities;
  const hasFacilities = facilities.length > 0;
  const isSellerSelected = facilities.some(
    (facility) => facility.id === selectedFacilityId
  );
  const organizationLabel = sellerLabel(organization);
  const facilityChoices = shipFromFacilityChoices(facilities);

  const toggleExpand = () => {
    onToggleExpand();
    if (
      !expanded &&
      facilityChoices.length === 1 &&
      selectedFacilityId === null
    ) {
      onSelectFacility(
        toSelectedSellerFacility(organization, facilityChoices[0])
      );
    }
  };

  return (
    <SellerAccordionRow
      testId={`add-to-cart-seller-row-${organization.id}`}
      disabled={!hasFacilities}
      expanded={expanded}
      onToggleExpand={toggleExpand}
      highlighted={isSellerSelected}
      expandedContent={
        <div className="border-t border-neut-border bg-neut-bg-alt px-4 py-2">
          <fieldset className="m-0 min-w-0 space-y-0.5 border-0 p-0">
            <legend className="sr-only">
              {t('inventory.buy_list.add_to_cart.facility_legend', {
                seller: organizationLabel,
              })}
            </legend>
            {facilityChoices.map(({ id, label }) => {
              const radioId = `add-to-cart-facility-${id}`;
              return (
                <label
                  key={id}
                  htmlFor={radioId}
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-neut-50"
                >
                  <input
                    id={radioId}
                    type="radio"
                    name="add-to-cart-seller-facility"
                    value={id}
                    checked={id === selectedFacilityId}
                    onChange={() =>
                      onSelectFacility(
                        toSelectedSellerFacility(organization, { id, label })
                      )
                    }
                    className="h-4 w-4 shrink-0 cursor-pointer accent-prim-600"
                  />
                  <span className="text-sm text-neut-prim">{label}</span>
                </label>
              );
            })}
          </fieldset>
        </div>
      }
    >
      <SellerRowHeader
        organizationLabel={organizationLabel}
        subtitleIcon={Warehouse}
        subtitle={
          hasFacilities
            ? t('inventory.buy_list.add_to_cart.facility_count', {
                count: facilities.length,
              })
            : t('inventory.buy_list.add_to_cart.no_facilities')
        }
      />
    </SellerAccordionRow>
  );
}

export function SelectSellerFacilityStep({
  selectedFacilityId,
  onSelectFacility,
}: Readonly<SelectSellerFacilityStepProps>) {
  const { t } = useTranslation('inventory');
  const [expandedOrganizationIds, setExpandedOrganizationIds] = useState<
    Set<string>
  >(() => new Set());
  const sellerPickerLabels = useBuyListSellerPickerLabels({
    loading: t('inventory.buy_list.add_to_cart.sellers_loading'),
    error: t('inventory.buy_list.add_to_cart.sellers_load_failed'),
    emptyMessage: t('inventory.buy_list.add_to_cart.no_sellers'),
  });

  return (
    <SellerOrganizationPicker
      testId="add-to-cart-sellers"
      labels={sellerPickerLabels}
      renderOrganization={(organization) => (
        <SellerFacilityRow
          organization={organization}
          expanded={expandedOrganizationIds.has(organization.id)}
          onToggleExpand={() =>
            setExpandedOrganizationIds((previous) =>
              toggleId(previous, organization.id)
            )
          }
          selectedFacilityId={selectedFacilityId}
          onSelectFacility={onSelectFacility}
        />
      )}
    />
  );
}
